import { useState, useEffect } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Upload, CheckCircle2, AlertCircle, Loader2, FileText } from "lucide-react";
import { useMatterStages, useUpdateMatterStage, useDocuments } from "@/hooks/use-supabase-queries";
import { toast } from "sonner";
import { format } from "date-fns";

interface StageDetailDrawerProps {
  stageId: string | null;
  matterId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function StageDetailDrawer({ stageId, matterId, open, onOpenChange }: StageDetailDrawerProps) {
  const { data: stages } = useMatterStages(matterId);
  const { data: documents } = useDocuments(matterId ? { matter_id: matterId } : undefined);
  const updateStage = useUpdateMatterStage();
  const stage = stages?.find(s => s.id === stageId);

  const [internalNotes, setInternalNotes] = useState('');
  const [clientExplanation, setClientExplanation] = useState('');

  // Sync when stage changes
  useEffect(() => {
    if (stage) {
      setInternalNotes(('internal_notes' in stage ? (stage as any).internal_notes : '') || '');
      setClientExplanation(stage.client_explanation || '');
    }
  }, [stage?.id, stage && 'internal_notes' in stage ? (stage as any).internal_notes : undefined, stage?.client_explanation]);

  const stageDocs = documents?.filter(d => d.stage_id === stageId) || [];

  const handleSave = async () => {
    if (!stageId) return;
    try {
      await updateStage.mutateAsync({
        id: stageId,
        internal_notes: internalNotes,
        client_explanation: clientExplanation,
      });
      toast.success("Stage notes saved");
    } catch (e: any) {
      toast.error("Update failed", { description: e.message });
    }
  };

  const statusBadge = {
    completed: { label: 'Completed', className: 'bg-primary/10 text-primary' },
    'in-progress': { label: 'In Progress', className: 'bg-accent text-accent-foreground' },
    'not-started': { label: 'Not Started', className: 'bg-muted text-muted-foreground' },
  };

  const currentBadge = statusBadge[(stage?.status as keyof typeof statusBadge) || 'not-started'] || statusBadge['not-started'];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {stage?.stage_name || 'Stage Details'}
            <Badge className={currentBadge.className}>{currentBadge.label}</Badge>
          </SheetTitle>
          <SheetDescription>
            {stage?.started_at && `Started: ${format(new Date(stage.started_at), 'MMM d, yyyy')}`}
            {stage?.completed_at && ` • Completed: ${format(new Date(stage.completed_at), 'MMM d, yyyy')}`}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Internal Notes</label>
              <Badge variant="outline" className="text-xs bg-destructive/10 text-destructive border-destructive/30">
                <AlertCircle className="h-3 w-3 mr-1" />Not visible to client
              </Badge>
            </div>
            <Textarea
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              placeholder="Add internal notes for your records..."
              className="min-h-[100px]"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Client-Facing Explanation</label>
              <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">Visible to client</Badge>
            </div>
            <Textarea
              value={clientExplanation}
              onChange={(e) => setClientExplanation(e.target.value)}
              placeholder="Explain this stage to the client in plain language..."
              className="min-h-[100px]"
            />
          </div>

          <Separator />

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Stage Documents</label>
              <span className="text-xs text-muted-foreground">{stageDocs.length} file{stageDocs.length !== 1 ? 's' : ''}</span>
            </div>
            {stageDocs.length > 0 ? (
              <div className="space-y-2">
                {stageDocs.map(doc => (
                  <div key={doc.id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/30">
                    <FileText className="h-4 w-4 text-primary shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{doc.name}</p>
                      <p className="text-xs text-muted-foreground">{doc.status} • {format(new Date(doc.created_at), 'MMM d')}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No documents for this stage yet</p>
              </div>
            )}
          </div>

          <Separator />

          <Button className="w-full" size="lg" onClick={handleSave} disabled={updateStage.isPending}>
            {updateStage.isPending ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <CheckCircle2 className="h-5 w-5 mr-2" />}
            Save Notes
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
